import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

var API = 'https://api.algrok.com';

function fmtCr(n){if(n==null||isNaN(n))return'--';var a=Math.abs(n);var s=n<0?'-':'';if(a>=1e6)return s+(a/1e6).toFixed(2)+' M Cr';if(a>=1e3)return s+(a/1e3).toFixed(2)+' K Cr';return s+a.toFixed(1)+' Cr';}
function fmtN(n){if(n==null||isNaN(n))return'--';return Math.round(n).toLocaleString('en-IN');}
function fmtSignedK(n){if(n==null||isNaN(n)||n===0)return'--';var sign=n<0?'-':'+';var a=Math.abs(n);if(a>=1e5)return sign+(a/1e5).toFixed(2)+'L';if(a>=1e3)return sign+(a/1e3).toFixed(1)+'k';return sign+a.toFixed(0);}
function fmtSignedPct(n){if(n==null||isNaN(n)||n===0)return'--';return (n>=0?'+':'')+n.toFixed(2);}
function fmtVOR(vol,oi){if(!vol||!oi)return'--';return (vol/oi).toFixed(2);}

// Label → display + colors
function labelMeta(label){
  if(label==='BUILDING_STRONG') return {txt:'BUILD ★',fg:'#22c55e',bg:'rgba(34,197,94,0.15)'};
  if(label==='BUILDING')        return {txt:'BUILD',  fg:'#16a34a',bg:'rgba(22,163,74,0.10)'};
  if(label==='UNWINDING_STRONG')return {txt:'UNWND ★',fg:'#fb923c',bg:'rgba(251,146,60,0.15)'};
  if(label==='UNWINDING')       return {txt:'UNWND',  fg:'#fb923c',bg:'rgba(251,146,60,0.08)'};
  if(label==='CHURN')           return {txt:'CHURN',  fg:'#a78bfa',bg:'rgba(167,139,250,0.15)'};
  if(label==='NEUTRAL')         return {txt:'·',      fg:'#6b7280',bg:'transparent'};
  return {txt:'--',fg:'#6b7280',bg:'transparent'};
}

// Tiny migration arrow + text
function MigrationCell({mig}){
  if(!mig||mig.open==null) return <span style={{fontSize:9,color:'#6b7280'}}>baseline</span>;
  var d=mig.delta||0;
  if(d===0) return <span style={{fontSize:10,color:'#9ca3af'}}>→ no change</span>;
  var color=d>0?'#22c55e':'#ef4444';
  var arrow=d>0?'↑':'↓';
  return <span style={{fontSize:10,color:color,fontWeight:700}}>{arrow} {d>0?'+':''}{d} pts</span>;
}

// Header strip metric card
function StripCard({label,value,sub,color}){
  return (
    <div style={{
      background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,
      padding:'8px 12px',minWidth:135,flex:'1 1 135px'
    }}>
      <div style={{fontSize:9,color:'#6b7280',fontWeight:700,letterSpacing:0.5,marginBottom:3}}>{label}</div>
      <div style={{fontSize:15,color:color||'#e5e7eb',fontWeight:800,lineHeight:1.1}}>{value}</div>
      <div style={{fontSize:10,color:'#9ca3af',marginTop:3,minHeight:13}}>{sub}</div>
    </div>
  );
}

function GammaExposure(){
  var [state,setState]=useState({});
  var [vrp,setVrp]=useState({});
  var [history,setHistory]=useState([]);
  var [strikeRange,setStrikeRange]=useState(20);
  var [showWalls,setShowWalls]=useState(true);
  var [showFlip,setShowFlip]=useState(true);
  var [sliderT,setSliderT]=useState(null);
  var [hidePanel,setHidePanel]=useState(false);
  var [flowTableRange,setFlowTableRange]=useState(10);
  var pollRef=useRef(null);

  var fetchState=useCallback(function(){
    if(sliderT) return;
    fetch(API+'/gex/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error){ setState(d); }
    }).catch(function(){});
    fetch(API+'/gex/history').then(function(r){return r.json();}).then(function(d){
      if(d && d.history){ setHistory(d.history); }
    }).catch(function(){});
    fetch(API+'/vrp/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error){ setVrp(d); }
    }).catch(function(){});
  },[sliderT]);

  useEffect(function(){
    fetchState();
    pollRef.current=setInterval(fetchState,5000);
    return function(){ if(pollRef.current) clearInterval(pollRef.current); };
  },[fetchState]);

  useEffect(function(){
    if(!sliderT) return;
    fetch(API+'/gex/snapshot?t='+encodeURIComponent(sliderT)).then(function(r){return r.json();}).then(function(d){
      if(d && !d.error){
        setState(function(prev){return Object.assign({},prev,{
          spot:d.spot, aggregates:d.aggregates,
          per_strike:(d.per_strike_compact||[]).map(function(p){return {strike:p[0],net_gex:p[1]};}),
          last_compute_time:d.time_hhmm,
          interpretation:prev.interpretation,
        });});
      }
    }).catch(function(){});
  },[sliderT]);

  var setExpiry=function(v){
    fetch(API+'/gex/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({expiry_choice:v})})
      .then(function(){fetchState();}).catch(function(){});
  };

  // ATM-centred displayStrikes for chart
  var displayStrikes=useMemo(function(){
    var s=state.per_strike||[];
    var spot=state.spot||0;
    if(!s.length||!spot) return [];
    if(!strikeRange) return s;
    var sorted=s.slice().sort(function(a,b){return a.strike-b.strike;});
    var atmIdx=0;
    var minDist=Infinity;
    sorted.forEach(function(r,i){var d=Math.abs(r.strike-spot);if(d<minDist){minDist=d;atmIdx=i;}});
    var lo=Math.max(0,atmIdx-strikeRange);
    var hi=Math.min(sorted.length,atmIdx+strikeRange+1);
    return sorted.slice(lo,hi);
  },[state.per_strike,state.spot,strikeRange]);

  var chartData=useMemo(function(){
    return displayStrikes.map(function(r){
      return {strike:r.strike, net_gex:r.net_gex, abs_gex:Math.abs(r.net_gex||0)};
    });
  },[displayStrikes]);

  // Flow table rows: ATM ± flowTableRange
  var flowRows=useMemo(function(){
    var s=state.per_strike||[];
    var spot=state.spot||0;
    if(!s.length||!spot) return [];
    var sorted=s.slice().sort(function(a,b){return a.strike-b.strike;});
    var atmIdx=0; var minDist=Infinity;
    sorted.forEach(function(r,i){var d=Math.abs(r.strike-spot);if(d<minDist){minDist=d;atmIdx=i;}});
    var lo=Math.max(0,atmIdx-flowTableRange);
    var hi=Math.min(sorted.length,atmIdx+flowTableRange+1);
    return sorted.slice(lo,hi).map(function(r){
      return Object.assign({},r,{_atm: r.strike===sorted[atmIdx].strike});
    });
  },[state.per_strike,state.spot,flowTableRange]);

  var sliderMinutes=useMemo(function(){return history.map(function(s){return s.time_hhmm;});},[history]);
  var sliderIdx=sliderT?Math.max(0,sliderMinutes.indexOf(sliderT)):sliderMinutes.length;
  var maxSlider=sliderMinutes.length;

  var agg=state.aggregates||{};
  var net=agg.net_gex;
  var netColor=net>0?'#22c55e':net<0?'#ef4444':'#9ca3af';
  var wm=state.walls_migration||{};
  var tm=state.top_movers||{};
  var topBuilder=tm.top_builder;
  var topUnwinder=tm.top_unwinder;
  var dd=state.dealer_delta;
  var ddOpen=state.dealer_delta_open;
  var acg=state.abs_chain_gamma;
  var acgDelta=state.abs_chain_gamma_delta;
  var vrpVal=vrp.vrp;
  var vrpRegime=vrp.regime||'';
  var vrpColor=vrpRegime==='RICH'?'#22c55e':vrpRegime==='CHEAP'?'#ef4444':'#9ca3af';

  var lbl={display:'flex',flexDirection:'column',gap:4,fontSize:11,color:'#9ca3af',fontWeight:600};
  var inp={background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'6px 8px',borderRadius:6,fontSize:12,fontWeight:600};

  return (
    <div style={{padding:16,minHeight:'calc(100vh - 80px)',color:'#e5e7eb'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <span style={{fontSize:18,fontWeight:800}}>📊 Gamma Exposure</span>
          <span style={{fontSize:11,color:'#9ca3af'}}>Net GEX <span style={{color:netColor,fontWeight:700}}>{fmtCr(net)}</span></span>
          <span style={{fontSize:11,color:'#9ca3af'}}>ABS GEX <span style={{color:'#e5e7eb',fontWeight:700}}>{fmtCr(agg.abs_gex)}</span></span>
          <span style={{fontSize:11,color:'#9ca3af'}}>Gamma Flip <span style={{color:'#22d3ee',fontWeight:700}}>{agg.gamma_flip||'--'}</span></span>
          <span style={{fontSize:11,color:'#9ca3af'}}>Net GEX Cross <span style={{color:'#fb923c',fontWeight:700}}>{agg.net_gex_cross||'--'}</span></span>
          {state.session_open_time?<span style={{fontSize:10,color:'#6b7280'}}>open {state.session_open_time}</span>:null}
        </div>
        <div style={{fontSize:11,color:'#9ca3af'}}>
          <span style={{marginRight:6}}>🕐 {state.last_compute_time||'--'}</span>
          <span style={{width:8,height:8,borderRadius:'50%',background:sliderT?'#9ca3af':'#ef4444',display:'inline-block'}}></span>
          {sliderT?<button onClick={function(){setSliderT(null);}} style={{marginLeft:8,background:'transparent',border:'1px solid #1f2937',color:'#e5e7eb',padding:'2px 8px',fontSize:10,borderRadius:4,cursor:'pointer'}}>LIVE</button>:null}
        </div>
      </div>

      {/* HEADER STRIP — walls migration + key metrics */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
        <StripCard label="CALL WALL" value={wm.call_wall&&wm.call_wall.current||agg.call_wall||'--'}
                   sub={<MigrationCell mig={wm.call_wall}/>} color="#22c55e"/>
        <StripCard label="GEX PIN" value={wm.gamma_flip&&wm.gamma_flip.current||agg.gamma_flip||'--'}
                   sub={<MigrationCell mig={wm.gamma_flip}/>} color="#22d3ee"/>
        <StripCard label="PUT FLOOR" value={wm.put_wall&&wm.put_wall.current||agg.put_wall||'--'}
                   sub={<MigrationCell mig={wm.put_wall}/>} color="#ef4444"/>
        <StripCard label="DEALER Δ" value={dd!=null?fmtCr(dd):'--'}
                   sub={ddOpen?'open '+fmtCr(ddOpen):''} color="#f472b6"/>
        <StripCard label="NET GEX" value={fmtCr(net)}
                   sub={net>0?'LONG GAMMA':net<0?'SHORT GAMMA':'NEUTRAL'} color={netColor}/>
        <StripCard label="Σ |CHAIN GAMMA|" value={fmtCr(acg)}
                   sub={acgDelta?'Δ '+(acgDelta>=0?'+':'')+fmtCr(acgDelta):''} color="#e5e7eb"/>
        <StripCard label="VOL RISK PREM" value={vrpVal!=null?(vrpVal>=0?'+':'')+vrpVal.toFixed(2)+'%':'--'}
                   sub={vrpRegime||(vrp.ready===false?'bootstrapping':'')} color={vrpColor}/>
        <StripCard label="ZERO GAMMA" value={agg.net_gex_cross||'--'}
                   sub={state.spot&&agg.net_gex_cross?((agg.net_gex_cross-state.spot)>0?'+':'')+(agg.net_gex_cross-state.spot).toFixed(0)+' pts':''}
                   color="#fb923c"/>
      </div>

      {/* Main grid: settings + chart */}
      <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:16}}>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,height:'fit-content'}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>Settings</span>
            <button onClick={function(){setHidePanel(!hidePanel);}} style={{background:'transparent',border:'none',color:'#6b7280',fontSize:14,cursor:'pointer'}}>{hidePanel?'»':'«'}</button>
          </div>
          {!hidePanel&&<>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,padding:'8px 10px',background:'#111827',borderRadius:6}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:'#1e3a8a',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700}}>50</div>
              <span style={{fontWeight:700}}>NIFTY</span>
            </div>
            <div style={lbl}>Expiry
              <select value={state.expiry_choice||'current_week'} onChange={function(e){setExpiry(e.target.value);}} style={inp}>
                <option value="current_week">Current week ({state.expiry_label||'--'})</option>
                <option value="next_week">Next week</option>
                <option value="month_end">Month end</option>
              </select>
            </div>
            <div style={{...lbl,marginTop:14}}>Strikes above-below ATM (chart)
              <div style={{display:'flex',gap:4}}>
                {[null,5,10,20].map(function(n){
                  var label=n===null?'All':String(n);
                  var active=strikeRange===n;
                  return <button key={label} onClick={function(){setStrikeRange(n);}} style={{
                    flex:1,padding:'6px 0',fontSize:11,fontWeight:700,
                    background:active?'#1e3a8a':'#0a0f1c',
                    color:active?'#93c5fd':'#9ca3af',
                    border:'1px solid '+(active?'#3b82f6':'#1f2937'),
                    borderRadius:4,cursor:'pointer'
                  }}>{label}</button>;
                })}
              </div>
            </div>
            <div style={{...lbl,marginTop:14}}>Flow table ATM±
              <div style={{display:'flex',gap:4}}>
                {[5,10,15,20].map(function(n){
                  var active=flowTableRange===n;
                  return <button key={n} onClick={function(){setFlowTableRange(n);}} style={{
                    flex:1,padding:'6px 0',fontSize:11,fontWeight:700,
                    background:active?'#1e3a8a':'#0a0f1c',
                    color:active?'#93c5fd':'#9ca3af',
                    border:'1px solid '+(active?'#3b82f6':'#1f2937'),
                    borderRadius:4,cursor:'pointer'
                  }}>{n}</button>;
                })}
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,fontSize:11,color:'#9ca3af',fontWeight:600}}>
              <span>Show Walls</span>
              <button onClick={function(){setShowWalls(!showWalls);}} style={{width:36,height:18,borderRadius:9,background:showWalls?'#3b82f6':'#374151',border:'none',position:'relative',cursor:'pointer'}}>
                <div style={{position:'absolute',top:2,left:showWalls?20:2,width:14,height:14,borderRadius:7,background:'#fff',transition:'left .15s'}}></div>
              </button>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:10,fontSize:11,color:'#9ca3af',fontWeight:600}}>
              <span>Show Flip</span>
              <button onClick={function(){setShowFlip(!showFlip);}} style={{width:36,height:18,borderRadius:9,background:showFlip?'#3b82f6':'#374151',border:'none',position:'relative',cursor:'pointer'}}>
                <div style={{position:'absolute',top:2,left:showFlip?20:2,width:14,height:14,borderRadius:7,background:'#fff',transition:'left .15s'}}></div>
              </button>
            </div>
          </>}
        </div>

        {/* Chart + interpretation */}
        <div>
          <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,minHeight:480}}>
            <div style={{height:480}}>
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{top:10,right:30,left:10,bottom:30}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
                  <XAxis dataKey="strike" stroke="#9ca3af" fontSize={10}/>
                  <YAxis yAxisId="bar" stroke="#9ca3af" fontSize={10} tickFormatter={function(v){return v.toFixed(1)+'L';}}/>
                  <Tooltip contentStyle={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,fontSize:11}}
                    formatter={function(v,name){return [v.toFixed(2)+' Cr',name];}}/>
                  <Bar yAxisId="bar" dataKey="net_gex" isAnimationActive={false}>
                    {chartData.map(function(d,i){return <Cell key={i} fill={d.net_gex>=0?'#16a34a':'#b91c1c'}/>;})}
                  </Bar>
                  <Line yAxisId="bar" type="monotone" dataKey="abs_gex" stroke="#3b82f6" strokeWidth={2} dot={false} isAnimationActive={false}/>
                  {state.spot&&<ReferenceLine yAxisId="bar" x={Math.round(state.spot/50)*50} stroke="#ffffff" strokeDasharray="3 3" label={{value:'Spot: '+state.spot.toFixed(1),fill:'#ffffff',fontSize:10,position:'top'}}/>}
                  {/* Each marker label owns a fixed row (dy stagger) so labels can
                      never collide when levels cluster within a few strikes.
                      Short prefixes keep rows compact: PW / Cross / Flip / CW. */}
                  {showWalls&&agg.put_wall&&<ReferenceLine yAxisId="bar" x={agg.put_wall} stroke="#ef4444" strokeDasharray="3 3" label={{value:'PW '+agg.put_wall,fill:'#ef4444',fontSize:10,position:'top',dy:10}}/>}
                  {showFlip&&agg.net_gex_cross&&<ReferenceLine yAxisId="bar" x={Math.round(agg.net_gex_cross/50)*50} stroke="#fb923c" strokeDasharray="2 4" label={{value:'Cross '+agg.net_gex_cross,fill:'#fb923c',fontSize:10,position:'top',dy:24}}/>}
                  {showFlip&&agg.gamma_flip&&<ReferenceLine yAxisId="bar" x={agg.gamma_flip} stroke="#22d3ee" strokeDasharray="3 3" label={{value:'Flip '+agg.gamma_flip,fill:'#22d3ee',fontSize:10,position:'top',dy:38}}/>}
                  {showWalls&&agg.call_wall&&<ReferenceLine yAxisId="bar" x={agg.call_wall} stroke="#22c55e" strokeDasharray="3 3" label={{value:'CW '+agg.call_wall,fill:'#22c55e',fontSize:10,position:'top',dy:52}}/>}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{marginTop:14,display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontSize:10,color:'#6b7280',fontWeight:700,width:46}}>TIME</span>
              <button onClick={function(){setSliderT(null);}} style={{background:'transparent',border:'1px solid #1f2937',color:'#9ca3af',padding:'4px 10px',fontSize:10,borderRadius:4,cursor:'pointer'}}>Reset</button>
              <span style={{fontSize:10,color:'#9ca3af',width:50}}>{sliderMinutes[0]||'9:15 AM'}</span>
              <input type="range" min={0} max={maxSlider} value={sliderIdx} onChange={function(e){
                var i=+e.target.value;
                if(i>=maxSlider) setSliderT(null);
                else setSliderT(sliderMinutes[i]);
              }} style={{flex:1}}/>
              <span style={{fontSize:10,color:'#9ca3af',width:60}}>{sliderT||sliderMinutes[sliderMinutes.length-1]||'3:30 PM'}</span>
            </div>
          </div>
          <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,marginTop:12}}>
            <div style={{fontSize:11,fontWeight:700,color:'#fbbf24',marginBottom:6}}>🧭 Automatic Interpretation</div>
            <div style={{fontSize:12,color:'#cbd5e1',lineHeight:1.6}}>{state.interpretation||'Waiting for live data — market closed or no chain available yet.'}</div>
          </div>
        </div>
      </div>

      {/* FLOW HEADLINE STRIP */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,marginTop:12,display:'flex',gap:24,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <span style={{fontSize:18}}>🟢</span>
          <div>
            <div style={{fontSize:9,color:'#6b7280',fontWeight:700,letterSpacing:0.5}}>TOP BUILDER</div>
            {topBuilder?<div style={{fontSize:13,color:'#22c55e',fontWeight:800}}>
              {topBuilder.strike}{topBuilder.side==='ce'?'C':'P'} <span style={{fontSize:11,color:'#9ca3af',fontWeight:600}}>ΔOI {fmtSignedK(topBuilder.oi_delta)}</span>
            </div>:<div style={{fontSize:13,color:'#6b7280'}}>--</div>}
          </div>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <span style={{fontSize:18}}>🔴</span>
          <div>
            <div style={{fontSize:9,color:'#6b7280',fontWeight:700,letterSpacing:0.5}}>TOP UNWINDER</div>
            {topUnwinder?<div style={{fontSize:13,color:'#fb923c',fontWeight:800}}>
              {topUnwinder.strike}{topUnwinder.side==='ce'?'C':'P'} <span style={{fontSize:11,color:'#9ca3af',fontWeight:600}}>ΔOI {fmtSignedK(topUnwinder.oi_delta)}</span>
            </div>:<div style={{fontSize:13,color:'#6b7280'}}>--</div>}
          </div>
        </div>
        <div style={{flex:1,minWidth:200,fontSize:11,color:'#9ca3af',lineHeight:1.5}}>
          {state.narrative||'Flow narrative pending — needs session-open baseline.'}
        </div>
      </div>

      {/* Δ FLOW MONITOR TABLE */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,marginTop:12,overflowX:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:700,color:'#e5e7eb'}}>📈 GEX Δ Flow Monitor</span>
          <span style={{fontSize:10,color:'#6b7280'}}>
            Δ basis: <span style={{color:'#93c5fd',fontWeight:700}}>since session open</span> · {state.session_open_time||'--'}
            <span style={{marginLeft:8,color:'#4b5563'}}>(rolling-30min in Phase 3)</span>
          </span>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
          <thead>
            <tr style={{color:'#6b7280',borderBottom:'1px solid #1f2937'}}>
              <th colSpan="5" style={{textAlign:'center',padding:'6px 0',color:'#22c55e',fontSize:11,letterSpacing:0.5}}>CALLS</th>
              <th style={{textAlign:'center',padding:'6px 8px',color:'#e5e7eb',fontSize:11}}>STRIKE</th>
              <th colSpan="5" style={{textAlign:'center',padding:'6px 0',color:'#ef4444',fontSize:11,letterSpacing:0.5}}>PUTS</th>
            </tr>
            <tr style={{color:'#9ca3af',borderBottom:'1px solid #1f2937'}}>
              <th style={{padding:'5px 6px',textAlign:'center'}}>Label</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>VOR</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔIV</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔOI</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔVol</th>
              <th style={{padding:'5px 8px',textAlign:'center',color:'#e5e7eb',fontWeight:700}}>K</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔVol</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔOI</th>
              <th style={{padding:'5px 6px',textAlign:'right'}}>ΔIV</th>
              <th style={{padding:'5px 6px',textAlign:'left'}}>VOR</th>
              <th style={{padding:'5px 6px',textAlign:'center'}}>Label</th>
            </tr>
          </thead>
          <tbody>
            {flowRows.length===0?<tr><td colSpan="11" style={{textAlign:'center',padding:20,color:'#6b7280'}}>Waiting for data...</td></tr>:flowRows.map(function(r){
              var cm=labelMeta(r.ce_label);
              var pm=labelMeta(r.pe_label);
              var rowBg=r._atm?'rgba(59,130,246,0.08)':'transparent';
              return (
                <tr key={r.strike} style={{borderBottom:'1px solid #111827',background:rowBg}}>
                  <td style={{padding:'4px 6px',textAlign:'center'}}>
                    <span style={{background:cm.bg,color:cm.fg,padding:'2px 5px',borderRadius:3,fontWeight:700,fontSize:9}}>{cm.txt}</span>
                  </td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:'#cbd5e1'}}>{fmtVOR(r.ce_volume,r.ce_oi)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:r.ce_iv_delta>0?'#22c55e':r.ce_iv_delta<0?'#ef4444':'#6b7280'}}>{fmtSignedPct(r.ce_iv_delta)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:r.ce_oi_delta>0?'#22c55e':r.ce_oi_delta<0?'#fb923c':'#6b7280',fontWeight:700}}>{fmtSignedK(r.ce_oi_delta)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:'#9ca3af'}}>{fmtSignedK(r.ce_vol_delta)}</td>
                  <td style={{padding:'4px 8px',textAlign:'center',color:r._atm?'#93c5fd':'#e5e7eb',fontWeight:r._atm?800:700,fontSize:11}}>{r.strike}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:'#9ca3af'}}>{fmtSignedK(r.pe_vol_delta)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:r.pe_oi_delta>0?'#22c55e':r.pe_oi_delta<0?'#fb923c':'#6b7280',fontWeight:700}}>{fmtSignedK(r.pe_oi_delta)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right',color:r.pe_iv_delta>0?'#22c55e':r.pe_iv_delta<0?'#ef4444':'#6b7280'}}>{fmtSignedPct(r.pe_iv_delta)}</td>
                  <td style={{padding:'4px 6px',textAlign:'left',color:'#cbd5e1'}}>{fmtVOR(r.pe_volume,r.pe_oi)}</td>
                  <td style={{padding:'4px 6px',textAlign:'center'}}>
                    <span style={{background:pm.bg,color:pm.fg,padding:'2px 5px',borderRadius:3,fontWeight:700,fontSize:9}}>{pm.txt}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{marginTop:10,fontSize:9,color:'#4b5563',display:'flex',gap:14,flexWrap:'wrap'}}>
          <span><span style={{color:'#22c55e'}}>BUILD ★</span> = strong OI build</span>
          <span><span style={{color:'#16a34a'}}>BUILD</span> = OI build</span>
          <span><span style={{color:'#fb923c'}}>UNWND ★</span> = strong unwind</span>
          <span><span style={{color:'#a78bfa'}}>CHURN</span> = high vol, low net OI</span>
          <span style={{color:'#6b7280'}}>· = neutral</span>
          <span style={{color:'#6b7280'}}>VOR = volume / OI</span>
        </div>
      </div>
    </div>
  );
}

export default GammaExposure;
